import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/pages/Blog";

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Get related posts (same category, excluding current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 2);

  // Get next and previous posts
  const currentIndex = blogPosts.findIndex(p => p.id === post.id);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <div data-testid="blog-post-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>{`${post.title} | Barton Blog`}</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={`https://barton.uk/blog/${post.slug}`} />
        <meta property="og:image" content={post.image} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image} />
      </Helmet>
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-16">
        {/* Back Link */}
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-pink-400 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        {/* Post Header */}
        <div className="max-w-4xl mx-auto">
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 text-sm font-medium mb-6">
            {post.categoryLabel}
          </span>
          
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-white">
            {post.title}
          </h1>

          <div className="flex items-center gap-4 text-white/50 text-sm mb-8">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" /> {post.date}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> {post.readTime}
            </span>
          </div>

          {/* Featured Image */}
          <div className="relative rounded-2xl overflow-hidden mb-12">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-[300px] md:h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>

          {/* Post Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            {post.content.split('\n\n').map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return (
                  <h2 key={index} className="font-heading text-2xl font-bold text-white mt-10 mb-4">
                    {paragraph.replace('## ', '')}
                  </h2>
                );
              }
              if (paragraph.startsWith('### ')) {
                return (
                  <h3 key={index} className="font-heading text-xl font-bold text-white mt-8 mb-3">
                    {paragraph.replace('### ', '')}
                  </h3>
                );
              }
              if (paragraph.startsWith('- ')) {
                const items = paragraph.split('\n').filter(line => line.startsWith('- '));
                return (
                  <ul key={index} className="list-disc list-inside space-y-2 text-white/70 my-4">
                    {items.map((item, i) => (
                      <li key={i}>{item.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '$1')}</li>
                    ))}
                  </ul>
                );
              }
              if (paragraph.match(/^\d+\./)) {
                const items = paragraph.split('\n').filter(line => line.match(/^\d+\./));
                return (
                  <ol key={index} className="list-decimal list-inside space-y-2 text-white/70 my-4">
                    {items.map((item, i) => (
                      <li key={i}>{item.replace(/^\d+\.\s*/, '')}</li>
                    ))}
                  </ol>
                );
              }
              return (
                <p key={index} className="text-white/70 leading-relaxed my-4">
                  {paragraph}
                </p>
              );
            })}
          </div>

          {/* Post Navigation */}
          <div className="border-t border-white/10 mt-16 pt-8">
            <div className="grid md:grid-cols-2 gap-6">
              {prevPost ? (
                <Link 
                  to={`/blog/${prevPost.slug}`}
                  className="group p-6 rounded-xl bg-[#111111] border border-white/10 hover:border-pink-500/50 transition-all"
                >
                  <span className="text-white/40 text-sm flex items-center gap-2 mb-2">
                    <ArrowLeft className="w-4 h-4" /> Previous
                  </span>
                  <h4 className="font-heading font-bold text-white group-hover:text-pink-400 transition-colors line-clamp-2">
                    {prevPost.title}
                  </h4>
                </Link>
              ) : <div />}
              
              {nextPost && (
                <Link 
                  to={`/blog/${nextPost.slug}`}
                  className="group p-6 rounded-xl bg-[#111111] border border-white/10 hover:border-purple-500/50 transition-all text-right"
                >
                  <span className="text-white/40 text-sm flex items-center gap-2 justify-end mb-2">
                    Next <ArrowRight className="w-4 h-4" />
                  </span>
                  <h4 className="font-heading font-bold text-white group-hover:text-purple-400 transition-colors line-clamp-2">
                    {nextPost.title}
                  </h4>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-[#0A0A0A] py-16 md:py-24">
          <div className="container-custom">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-8">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost, index) => (
                <Link 
                  to={`/blog/${relatedPost.slug}`}
                  key={relatedPost.id}
                  className={`group bg-[#111111] rounded-2xl overflow-hidden border border-white/10 transition-all duration-300 hover:-translate-y-2 block ${
                    index === 0 ? "hover:border-pink-500/50" : "hover:border-purple-500/50"
                  }`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent" />
                  </div>
                  <div className="p-6">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                      index === 0 ? "bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400" : "bg-gradient-to-r from-blue-500 to-purple-500/10 text-purple-400"
                    }`}>
                      {relatedPost.categoryLabel}
                    </span>
                    <h3 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-pink-400 transition-colors">
                      {relatedPost.title}
                    </h3>
                    <div className="flex items-center gap-3 text-white/40 text-xs">
                      <span>{relatedPost.date}</span>
                      <span>{relatedPost.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="relative rounded-3xl p-8 md:p-16 text-center text-white overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-transparent to-pink-500/20" />
            <div className="absolute inset-0 border border-white/10 rounded-3xl" />
            
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Need help implementing this?
              </h2>
              <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
                Let us handle the hard work. Get in touch and we'll help you put these strategies into action.
              </p>
              <Button
                asChild
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full px-10 py-6 text-lg"
              >
                <Link to="/contact">
                  Get in Touch <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
